import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { ModalType, useModal } from 'src/providers/ModalProvider'
import { NotificationType, notify } from 'src/providers/NotificationProvider'
import { useUser } from 'src/providers/UserProvider'

import { Button } from 'src/components/atoms/Button'
import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { DefinitionListItem } from 'src/components/atoms/DefinitionListItem'
import { InternalLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Heading3, Paragraph } from 'src/components/atoms/Typography'
import { Heading1 } from 'src/components/atoms/Typography/Headers'
import { Page } from 'src/components/layouts/Page/DashboardPage'
import {
  Action,
  ActionsDropdown
} from 'src/components/molecules/ActionsDropdown'
import {
  FundingHistoryNav,
  FundingHistoryNavTab
} from 'src/components/organisms/navs/FundingHistoryNav'
import { VStack } from 'src/components/templates/VStack'

import {
  namedOperations,
  useGetAccountSubscriptionDetailsQuery,
  useUpdatePaymentMethodMutation
} from 'src/config/generated/graphql'
import {
  updateAccountPaymentMethodPath,
  viewAccountPaymentMethodsPath
} from 'src/config/paths'
import { withAuthRequired } from 'src/utils/auth'

const ViewAccountPaymentMethods = () => {
  const { user } = useUser()
  const { openModal } = useModal()

  const { data, loading } = useGetAccountSubscriptionDetailsQuery({
    variables: { funderId: user?.Account?.FunderId }
  })
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation({
    refetchQueries: [
      namedOperations.Query.getAccountSubscriptionDetails,
      namedOperations.Query.getSubscription
    ]
  })

  const router = useRouter()

  const subscription = useMemo(() => data?.Subscription[0], [data])

  const removePaymentMethod = async () => {
    updatePaymentMethod({
      variables: {
        input: {
          Id: user?.Account?.Id,
          SubscriptionId: subscription?.Id
        }
      },
      onError: error => {
        notify({
          message: `There was a problem while removing your payment method, please try again:\n${error.message}`,
          type: NotificationType.error
        })
      },
      onCompleted: () => {
        notify({
          message: `Your payment method was successfully removed.`,
          type: NotificationType.success
        })
        router.push(viewAccountPaymentMethodsPath)
      }
    })
  }

  const actions: Action[] = user?.Account?.PaymentMethodDescriptor
    ? [
        {
          text: 'Delete/remove card',
          onClick: subscription?.Id
            ? () => openModal(ModalType.RemovePaymentMethod)
            : removePaymentMethod
        },
        {
          text: 'Edit card details',
          href: updateAccountPaymentMethodPath
        }
      ]
    : []

  return (
    <LoadingSpinner isLoading={loading}>
      <VStack>
        <FundingHistoryNav activeTab={FundingHistoryNavTab.Billing}>
          <Heading1 className='mb-4 pt-5 md:pt-10'>
            Edit payment method
          </Heading1>
          <Card>
            <div className='flex justify-between items-center pr-4 mb-6'>
              <Heading3 className='m-0! font-semibold'>
                Payment details
              </Heading3>

              {actions.length > 0 ? (
                <ActionsDropdown
                  actions={actions}
                  className='ml-4'
                  ButtonComponent={ButtonRebrand}
                  buttonComponentProps={{ variant: 'outline' }}
                >
                  Actions
                </ActionsDropdown>
              ) : null}
            </div>

            <Paragraph className='mb-4!'>
              {user?.Account?.PaymentMethodDescriptor ? (
                <dl>
                  <DefinitionListItem
                    title='Credit/Debit card'
                    data={user?.Account?.PaymentMethodDescriptor}
                  />{' '}
                </dl>
              ) : (
                <>
                  <Paragraph>You have no saved payment methods</Paragraph>
                  <InternalLink
                    className='mb-10'
                    href={updateAccountPaymentMethodPath}
                  >
                    <Button variant='outlined'>Add a credit/debit card</Button>
                  </InternalLink>
                </>
              )}
            </Paragraph>
          </Card>
        </FundingHistoryNav>
      </VStack>
    </LoadingSpinner>
  )
}

const ViewAccountPaymentMethodsPage = () => {
  return (
    <Page title='Payment methods'>
      <ViewAccountPaymentMethods />
    </Page>
  )
}

export default withAuthRequired(ViewAccountPaymentMethodsPage)
